import { cn } from "@/lib/utils"
import { PAGE_FOOTER_SLOT_ID, PAGE_INSET, slotRef } from "@/components/layout/PageSlots"

/**
 * The app's footer: the header's twin at the other end of the viewport, and empty until a page
 * puts something in it.
 *
 * `empty:hidden` is what makes it optional. The element is always in the tree — it has to be,
 * as the portal target pages render into — but with no children it is `display: none`, takes no
 * space, and the shell (which asks `:has([data-page-footer]:not(:empty))`) lays the page out as
 * though it were not there. Filling it is the whole API; nothing has to be told a footer now
 * exists.
 *
 * Fixed and transparent like the header, so content passes underneath and dissolves into it —
 * see CHROME_MASK in lib/fade.ts, which AppShell applies to the scrollport.
 */
export function AppFooter({ className }: { className?: string }) {
  return (
    <footer
      id={PAGE_FOOTER_SLOT_ID}
      ref={slotRef(PAGE_FOOTER_SLOT_ID)}
      // Read by AppShell's `group-has-*` variants to decide whether to reserve room and fade.
      data-page-footer
      className={cn(
        "fixed inset-x-0 bottom-0 z-30 flex h-16 flex-row items-center gap-6",
        // Was px-4 md:px-8, half the header's. See PAGE_INSET.
        PAGE_INSET,
        "empty:hidden",
        // Same reason as the header's slot: a footer-heavy page shrinks rather than pushing
        // its own controls off the edge of a narrow window.
        "min-w-0 overflow-x-auto [scrollbar-width:none]",
        className,
      )}
    />
  )
}
